import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FieldGrid } from "@/components/onboarding/FormFields";
import { StepFooter } from "@/components/onboarding/StepFooter";

const COMPANY_FIELDS = [
  { k: "display_name", label: "Business name customers know you by", ph: "Summit Peak Roofing" },
  { k: "legal_name", label: "Legal business name (as registered with the IRS)", ph: "Summit Peak Roofing LLC" },
  { k: "ein", label: "EIN (Federal Tax ID)", ph: "12-3456789", hint: "Required by carriers to register your texting. Stored securely; only the last 4 digits are shown." },
  { k: "website", label: "Business website", ph: "https://summitpeakroofing.com" },
  { k: "support_email", label: "Public customer support email", ph: "help@summitpeakroofing.com", hint: "Shown on your SMS consent and privacy pages." },
  { k: "address.line1", label: "Business street address", ph: "1200 Main St", wide: true },
  { k: "address.city", label: "City", ph: "Tampa" },
  { k: "address.state", label: "State", ph: "FL" },
  { k: "address.postal_code", label: "ZIP", ph: "33601" },
];
const TYPES = ["LLC", "Corporation", "Partnership", "Sole Proprietorship", "Non-profit"];

export function CompanyStep({ initial, onSave, busy }) {
  const [v, setV] = useState({ business_type: "LLC", ...initial, ein: "" });
  return (
    <div className="space-y-5" data-testid="onboarding-step-company">
      <div>
        <Label className="text-sm mb-2 block text-slate-300">Business type</Label>
        <Select value={v.business_type} onValueChange={(x) => setV({ ...v, business_type: x })}>
          <SelectTrigger className="bg-slate-950 border-slate-700 sm:w-1/2" data-testid="onboarding-company-business-type"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <FieldGrid fields={COMPANY_FIELDS} values={v} onChange={setV} prefix="onboarding-company" />
      {initial?.ein_on_file && <p className="text-xs text-emerald-400">EIN on file ({initial.ein}). Re-enter it to save changes.</p>}
      <StepFooter busy={busy} onSave={() => onSave(v)} />
    </div>
  );
}

const CONTACT_FIELDS = [
  { k: "first_name", label: "First name" }, { k: "last_name", label: "Last name" },
  { k: "title", label: "Job title", ph: "Owner" }, { k: "email", label: "Business email", type: "email" },
  { k: "phone", label: "Mobile phone", ph: "(555) 555-0100", hint: "Carriers may contact this person to verify the business." },
];

export function ContactStep({ initial, onSave, busy }) {
  const [v, setV] = useState(initial || {});
  return (
    <div className="space-y-5" data-testid="onboarding-step-contact">
      <p className="text-sm text-slate-400">The authorized representative who can legally act for the business.</p>
      <FieldGrid fields={CONTACT_FIELDS} values={v} onChange={setV} prefix="onboarding-contact" />
      <StepFooter busy={busy} onSave={() => onSave(v)} />
    </div>
  );
}

const PHONE_OPTS = [
  ["new_local", "Get a new local number from RoofLead", "Fastest. We set it up after your texting is approved."],
  ["port_existing", "Move (port) my existing business number", "Keeps your number. Porting usually takes 1–3 weeks."],
  ["undecided", "Not sure yet", "We'll help you decide during setup."],
];

export function PhoneStep({ initial, onSave, busy }) {
  const [v, setV] = useState({ preference: "new_local", ...initial });
  const fields = [
    ...(v.preference === "new_local" ? [{ k: "area_code", label: "Preferred area code", ph: "813" }] : []),
    ...(v.preference === "port_existing" ? [{ k: "existing_number", label: "Number to move", ph: "(813) 555-0100" }] : []),
    { k: "forward_to_number", label: "Cell to ring first (optional)", ph: "(813) 555-0199", hint: "Calls ring here first; missed calls go to RoofLead." },
  ];
  return (
    <div className="space-y-6" data-testid="onboarding-step-phone">
      <RadioGroup value={v.preference} onValueChange={(x) => setV({ ...v, preference: x })} className="space-y-3">
        {PHONE_OPTS.map(([k, t, d]) => (
          <label key={k} className={`flex gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${v.preference === k ? "border-orange-500/50 bg-orange-500/5" : "border-slate-800 hover:border-slate-700"}`}>
            <RadioGroupItem value={k} data-testid={`onboarding-phone-${k}`} className="mt-1" />
            <div><p className="text-slate-100 font-medium">{t}</p><p className="text-xs text-slate-500 mt-0.5">{d}</p></div>
          </label>
        ))}
      </RadioGroup>
      <FieldGrid fields={fields} values={v} onChange={setV} prefix="onboarding-phone" />
      <p className="text-xs text-slate-500">Nothing is purchased or changed in Twilio at this step.</p>
      <StepFooter busy={busy} onSave={() => onSave(v)} />
    </div>
  );
}

export function NotificationsStep({ initial, onSave, busy }) {
  const [v, setV] = useState({ consent_ack: false, ...initial });
  return (
    <div className="space-y-6" data-testid="onboarding-step-notifications">
      <FieldGrid fields={[{ k: "notification_email", label: "Send new-lead and booking alerts to", type: "email", wide: true }]}
        values={v} onChange={setV} prefix="onboarding-notifications" />
      <label className="flex gap-3 items-start p-4 rounded-lg border border-slate-800 bg-slate-950/50">
        <Checkbox checked={v.consent_ack} onCheckedChange={(c) => setV({ ...v, consent_ack: !!c })} data-testid="onboarding-consent-ack" className="mt-0.5" />
        <span className="text-sm text-slate-300">
          I understand RoofLead only texts homeowners who opt in (pressing 1 on a missed call or checking the web-form box), honors
          STOP/HELP immediately, and that we will not text purchased lists or anyone who hasn't consented.
        </span>
      </label>
      <StepFooter busy={busy} onSave={() => onSave(v)} />
    </div>
  );
}
